export const processChartData = <T extends number | number[]>(data: T, allowNegative?: boolean, allowDecimal?: boolean): T => {
  const processCallback = (val: number) => {
    if (!val) { return val; }
    if (!allowNegative) { val = Math.max(0, val); }
    if (!allowDecimal) { val = Math.round(val); }
    return val;
  };
  return (Array.isArray(data) ? (data as number[]).map(processCallback) : processCallback(data as number)) as T;
};

type AnyObj = Record<string, any>;

/**
 * Recursively mirror `shape`. When encountering a string[],
 * if `select(path)` returns true, expand it to Record<string, V> and
 * initialize each item with `init`. Otherwise:
 *  - if `copyUnselectedArrays` = true, keep the original array
 *  - else, omit (return undefined).
 */
export function buildFromShapeSelective<V>(
  shape: AnyObj,
  init: V | ((path: string[], leafName: string | null) => V),
  select: (path: string[]) => boolean,
  options?: { copyUnselectedArrays?: boolean }
): any {
  const { copyUnselectedArrays = false } = options ?? {};

  const walk = (node: any, path: string[]): any => {
    if (Array.isArray(node)) {
      if (select(path)) {
        // Expect array<string>; expand into record
        const rec: Record<string, V> = {};
        for (const name of node) {
          const value =
            typeof init === 'function'
              ? (init as any)(path, String(name))
              : init;
          rec[String(name)] = value;
        }
        return rec;
      }
      return copyUnselectedArrays ? node.slice() : undefined;
    }

    if (node && typeof node === 'object') {
      const out: AnyObj = {};
      for (const k of Object.keys(node)) {
        const child = walk(node[k], [...path, k]);
        if (child !== undefined) out[k] = child;
      }
      return out;
    }

    // primitives: initialize only if selector says this path is a leaf to init
    if (select(path)) {
      return typeof init === 'function' ? (init as any)(path, null) : init;
    }
    return undefined;
  };

  return walk(shape, []);
}

/**
 * Collect keys of the object located at a (possibly wildcard) path.
 *
 * Use "*" to match ALL keys at that level.
 * - Example: ["*", "*", "gates"] → for every building and floor, get keys of the "gates" object.
 * - Example: ["BuildingA", "*", "gates"] → all floors in BuildingA.
 * - Example: ["*", "Floor1", "gates"] → Floor1 across all buildings.
 *
 * @param obj - The root object to traverse.
 * @param path - Array of keys; any element can be the string "*".
 * @param options - Optional flags.
 *   - withPaths: if true, return items with their full path instead of plain keys.
 *
 * @returns If withPaths = false (default): string[] of keys at the final objects.
 *          If withPaths = true: Array<{ path: string[]; key: string }>
 *          where path is the path to the container object (e.g., [building, floor, "gates"]).
 *
 * @example
 * // All gate names across all buildings/floors:
 * collectKeysAtWildcardPath(gatesOnly, ["*", "*", "gates"]);
 *
 * @example
 * // Gate names + where they came from:
 * collectKeysAtWildcardPath(gatesOnly, ["*", "*", "gates"], { withPaths: true });
 */
export function collectKeysAtPath(
  obj: any,
  path: (string | "*")[],
  options?: { withPaths?: boolean }
): string[] | Array<{ path: string[]; key: string }> {
  const withPaths = !!options?.withPaths;
  const resultsKeys: string[] = [];
  const resultsWithPaths: Array<{ path: string[]; key: string }> = [];

  const dfs = (node: any, idx: number, curPath: string[]) => {
    if (idx >= path.length) {
      // We are at the container object we want to read keys from
      if (node && typeof node === "object") {
        for (const k of Object.keys(node)) {
          if (withPaths) {
            resultsWithPaths.push({ path: curPath, key: k });
          } else {
            resultsKeys.push(k);
          }
        }
      }
      return;
    }

    const seg = path[idx];

    if (seg === "*") {
      if (!node || typeof node !== "object") return;
      for (const k of Object.keys(node)) {
        dfs(node[k], idx + 1, [...curPath, k]);
      }
    } else {
      if (!node || typeof node !== "object") return;
      dfs(node[seg], idx + 1, [...curPath, String(seg)]);
    }
  };

  dfs(obj, 0, []);

  return withPaths ? resultsWithPaths : resultsKeys;
}

/**
 * Build a lookup index for quick access to leaf area data inside a nested
 * building → floor → bucket ("gates" | "zones" | "parking") structure.
 *
 * The resulting index maps each area key (e.g. "gate_01") directly to its
 * corresponding data object ({ entrance, exit, average_timespent }), so you
 * can update it in O(1) time instead of looping through buildings/floors.
 *
 * @param obj - The nested object (e.g. `gatesOnly`) with shape:
 *              { [building]: { [floor]: { [bucket]: { [areaKey]: AreaData } } } }
 * @param bucket - The bucket name to index ("gates" | "zones" | "parking").
 *
 * @returns A flat index object: { [areaKey]: AreaData }
 *
 * @example
 * type AreaData = { entrance: number[]; exit: number[]; average_timespent: number[] };
 *
 * const gatesIndex = buildIndex(gatesOnly, "gates");
 *
 * // Now you can update directly:
 * gatesIndex["gate_01"].entrance.push(42);
 */
export function buildIndex<T>(
  obj: any,
  bucket: "gates" | "zones" | "parking"
): Record<string, T> {
  const index: Record<string, T> = {};
  for (const building in obj) {
    for (const floor in obj[building]) {
      const bucketObj = obj[building][floor][bucket];
      if (bucketObj) {
        for (const areaKey in bucketObj) {
          index[areaKey] = bucketObj[areaKey];
        }
      }
    }
  }
  return index;
}